import React, { useState } from 'react';
import _isEmpty from 'lodash-es/isEmpty';
import Input from 'components/Form/InputGroup';
import Select from 'components/Form/Select2';
import Button from 'components/Buttons/Button';
import {
  CustomFieldDefinition,
  CustomFieldDefinitionDefaults,
  FieldType,
} from 'types/customField';
import Label from 'components/Form/Label';
import { ListInputsWithColors } from 'containers/Eleves/List/components/ListInputsWithColors';
import DeleteButton from 'components/Buttons/DeleteButton';
import { SparklesIcon, TrashIcon as Trash } from '@heroicons/react/24/outline';
import 'twin.macro';
import { useTranslation } from 'react-i18next';
import { useCustomFieldPresets } from 'queries/useCustomFieldPresets';
import FadeInComponent from 'components/Transitions/FadeIn';
import { CustomFieldPresetPreview } from './CustomFieldPresetPreview';
import { getPluralName } from 'utils/getPluralName';

interface Props {
  onClose: () => void;
  onSubmit: (v: Omit<CustomFieldDefinition, 'id'>) => Promise<void>;
  onDelete?: (id: string) => void;
  initialValues: Omit<CustomFieldDefinition, 'id'>;
  customFieldId?: string;
  availableFieldTypes: FieldType[];
  cycles: string[];
}

export function CustomFieldForm(props: Props) {
  const { onClose, customFieldId, onDelete, availableFieldTypes, cycles } =
    props;
  const { t } = useTranslation(['common']);
  const [formView, setFormView] = useState<'manual' | 'examples'>('manual');
  const [field, setField] = useState<Omit<CustomFieldDefinition, 'id'>>(
    props.initialValues,
  );
  const [error, setError] = useState<string | undefined>(undefined);

  const { data: customFieldPresets = [] } = useCustomFieldPresets({
    target: 'eleve',
    cycles,
  });
  const hasPlural = CustomFieldDefinitionDefaults[field.type].hasPluralName;

  if (formView === 'examples') {
    return (
      <div tw="flex flex-col gap-y-4">
        {customFieldPresets.map(f => (
          <CustomFieldPresetPreview
            key={f.id}
            customField={f}
            onSelect={v => props.onSubmit(v).then(() => onClose())}
          />
        ))}
        <div tw="flex justify-end">
          <Button
            minimal
            type="button"
            small
            onClick={() => setFormView('manual')}
          >
            {t('common:buttons.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div tw="p-1">
        <Input
          id="custom-field-name"
          label={t('common:components.customFields.fieldNameLabel')}
          autoFocus
          value={field.name || ''}
          autoComplete="off"
          error={error}
          onChange={name => {
            if (error) setError(undefined);
            const pluralName = getPluralName({
              prevSingularName: field.name,
              prevPluralName: field.pluralName,
              nextSingularName: name,
            });
            setField({ ...field, name, pluralName });
          }}
        />
        {hasPlural && (
          <Input
            id="custom-field-pluralname"
            label={t('common:components.customFields.fieldNamePluralLabel')}
            value={field.pluralName || ''}
            autoComplete="off"
            error={error}
            onChange={pluralName => {
              if (error) setError(undefined);
              setField({ ...field, pluralName });
            }}
          />
        )}
        <Select
          label={t('common:components.customFields.typeLabel')}
          id="custom-field-type-select"
          data-testid="custom-field-type-select"
          value={field.type || ''}
          portal
          onChange={value =>
            setField({
              ...field,
              type: value,
              pluralName: field.pluralName || field.name,
            })
          }
          options={availableFieldTypes.map(ft => ({
            value: ft,
            label: t(`common:customFields.types.${ft}`),
          }))}
        />
      </div>
      {field.type === 'select' && (
        <div tw="mt-2 p-1">
          <Label tw="mb-1">
            {t('common:components.customFields.categoryItems')}
          </Label>
          <div>
            <ListInputsWithColors
              inline
              items={field.options || []}
              onChange={value => setField({ ...field, options: value })}
            />
          </div>
        </div>
      )}
      <div tw="text-right mt-4 flex items-center gap-2 justify-end">
        {customFieldId && onDelete && (
          <DeleteButton
            withConfirmation
            onClick={() => {
              onDelete(customFieldId);
              onClose();
            }}
            minimal
            icon={Trash}
          />
        )}
        <Button minimal onClick={onClose}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          aria-label="Ajouter le champ"
          type="button"
          onClick={() => {
            if (_isEmpty((field.name || '').trim())) {
              setError(t('common:messages.fieldCantBeEmpty'));
              return;
            }
            setError(undefined);
            props.onSubmit(field).then(() => onClose());
          }}
        >
          {customFieldId ? t('common:buttons.modify') : t('common:buttons.add')}
        </Button>
      </div>
      {customFieldPresets.length > 0 && (
        <FadeInComponent>
          <div tw="mt-8">
            <Button
              icon={SparklesIcon}
              small
              minimal
              fill
              onClick={() => setFormView('examples')}
            >
              {t('common:components.customFields.chooseFromExamples')}
            </Button>
          </div>
        </FadeInComponent>
      )}
    </div>
  );
}
